"use client";

import { Globe, Mail, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DetailInfoRow } from "@/components/ui/detail-info-row";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { SOCIAL_PLATFORM_ICONS } from "@/components/ui/social-icons";
import {
  type ContactDetails,
  hasAnyContactDetail,
  SOCIAL_PLATFORMS,
  socialLinkDisplay,
  socialLinkHref,
  websiteDisplay,
  websiteHref,
} from "@/lib/contact-links";
import { formatPhoneForDisplay, phoneTelHref } from "@/lib/phone-number";

export { hasAnyContactDetail };

type Entry = {
  key: string;
  href: string;
  primary: string;
  secondary: string;
  Icon: React.ComponentType<{ className?: string }>;
  /** tel:/mailto: must not open a new tab. */
  external: boolean;
};

function buildEntries(details: ContactDetails, subjectLabel: string): Entry[] {
  const entries: Entry[] = [];

  const phone = details.contactPhone?.trim();
  if (phone) {
    entries.push({
      key: "phone",
      // Dialable from anywhere: a stored "98201 40079" still rings +91.
      href: phoneTelHref(phone) ?? `tel:${phone.replace(/\s+/g, "")}`,
      primary: formatPhoneForDisplay(phone) ?? phone,
      secondary: `Call ${subjectLabel}`,
      Icon: Phone,
      external: false,
    });
  }

  const email = details.contactEmail?.trim();
  if (email) {
    entries.push({
      key: "email",
      href: `mailto:${email}`,
      primary: email,
      secondary: `Email ${subjectLabel}`,
      Icon: Mail,
      external: false,
    });
  }

  const website = websiteHref(details.websiteUrl);
  if (website) {
    entries.push({
      key: "website",
      href: website,
      primary: websiteDisplay(details.websiteUrl),
      secondary: "Visit website",
      Icon: Globe,
      external: true,
    });
  }

  for (const platform of SOCIAL_PLATFORMS) {
    const raw = details.socialLinks?.[platform.id];
    const href = socialLinkHref(platform.id, raw);
    if (!href) continue;
    entries.push({
      key: platform.id,
      href,
      primary: socialLinkDisplay(platform.id, raw),
      secondary: platform.label,
      Icon: SOCIAL_PLATFORM_ICONS[platform.id],
      external: true,
    });
  }

  return entries;
}

/**
 * The tappable "Contact details" row. Renders nothing when there is nothing to
 * show, so callers can drop it in unconditionally.
 */
export function ContactDetailsRow({
  details,
  onOpen,
  subtitle,
  className,
}: {
  details: ContactDetails;
  onOpen: () => void;
  subtitle: string;
  className?: string;
}) {
  if (!hasAnyContactDetail(details)) return null;

  return (
    <DetailInfoRow
      icon={Phone}
      title="Contact & socials"
      subtitle={subtitle}
      onClick={onOpen}
      className={className}
    />
  );
}

/**
 * Shared contact drawer for hiives, venues, and events. Every entry is a real
 * link — `tel:`/`mailto:` stay in-app, profile links open in a new tab.
 */
export function ContactDetailsDrawer({
  open,
  onOpenChange,
  details,
  subjectLabel,
  title = "Contact details",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  details: ContactDetails;
  /** Used in the row captions: "Call the venue", "Email Orca". */
  subjectLabel: string;
  title?: string;
}) {
  const entries = buildEntries(details, subjectLabel);

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="z-1000 max-h-[85vh] border-border bg-surface text-text-primary shadow-xl dark:border-white/[0.14] dark:bg-[#0f1218] dark:text-white">
        <DrawerHeader className="border-b border-border px-4 py-3 text-center dark:border-white/[0.1]">
          <DrawerTitle className="text-center text-xl leading-tight text-text-primary dark:text-white">
            {title}
          </DrawerTitle>
        </DrawerHeader>
        <div className="space-y-4 overflow-y-auto p-4 pb-[max(env(safe-area-inset-bottom),1rem)]">
          <div className="grid gap-3">
            {entries.map((entry) => (
              <Button
                key={entry.key}
                asChild
                variant="outline"
                className="h-14 justify-start gap-4 rounded-2xl border-border bg-surface-alt px-4 text-left hover:bg-surface-elevated dark:border-glass-border-10 dark:bg-glass-bg-04 dark:hover:bg-glass-bg-08"
              >
                <a
                  href={entry.href}
                  {...(entry.external
                    ? { target: "_blank", rel: "noopener noreferrer" }
                    : {})}
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-surface dark:bg-glass-bg-08">
                    <entry.Icon className="h-5 w-5 text-warning dark:text-[#F5E6B8]" />
                  </div>
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate font-semibold text-text-primary dark:text-white">
                      {entry.primary}
                    </span>
                    <span className="text-xs text-text-tertiary dark:text-white/40">
                      {entry.secondary}
                    </span>
                  </div>
                </a>
              </Button>
            ))}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
